import type { TimeZone } from '@iheartjane/types';
import { timezone as parseApiTimezones } from '@iheartjane/utils';
import dayjs from 'dayjs';
import type { Dayjs, OptionType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjsDuration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { Time } from '@jane/shared/types';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(dayjsDuration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);

export function duration(
  time: number,
  unit: 'milliseconds' | 'seconds' | 'minutes'
) {
  return dayjs.duration(time, unit);
}

export const timeFromNow = (
  time: Time,
  inputFormat?: OptionType, // undefined uses ISO8601, without fraction seconds e.g. '2020-04-02T08:02:17-05:00'
  suffix = true, // adds 'ago'
  timezone?: TimeZone
): string => {
  if (timezone) {
    return dayjs(time, inputFormat)
      .tz(parseApiTimezones(timezone))
      .fromNow(!suffix);
  }

  return dayjs(time, inputFormat).fromNow(!suffix);
};

// for more information on inclusivity, see: https://day.js.org/docs/en/plugin/is-between
export const timeIsBetween = (
  time: Time,
  startTime: Time,
  endTime: Time,
  inclusivity?: '()' | '[]' | '[)' | '(]'
): boolean => dayjs(time).isBetween(startTime, endTime, null, inclusivity);

export const timeIsSameOrBefore = (
  time: Dayjs,
  timeToCheck: Dayjs,
  unit?: 'day'
): boolean => time.isSameOrBefore(timeToCheck, unit);

export const timeIsSameOrAfter = (time: Dayjs, timeToCheck: Dayjs): boolean =>
  time.isSameOrAfter(timeToCheck);
